import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from '../../app-config.service';
import { Router } from '@angular/router'
import { Spinner } from '../../models';
import { CommonMethods, Severity } from '../helpers';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private static readonly UNAUTHORIZED_SESSION_MESSAGE =
    'Your session has ended because this account was signed in from another device or location. Please sign in again to continue.';

  private currentUserSubject!: BehaviorSubject<any>;
  private authTokenReadySubject = new BehaviorSubject<boolean>(false);
  private isHandlingUnauthorizedSession = false;
  currentUser!: Observable<any>;
  readonly authTokenReady$ = this.authTokenReadySubject.asObservable();
  constructor(
    private _http: HttpClient,
    private _router: Router,
    private _configService: ConfigService,
    private _commonMethods: CommonMethods
  ) {
    const currentUser = localStorage.getItem('currentBIUser') ? JSON.parse(localStorage.getItem('currentBIUser')!) : {};
    let currentUserData = {};//(currentUser !== null && currentUser !== 'undefined' && currentUser !== '') ? JSON.parse(currentUser) : {};
    this.currentUserSubject = new BehaviorSubject<any>(currentUserData);
    this.currentUser = this.currentUserSubject.asObservable();
  }

  /**
   * This method is used to return the base url
   * @returns 
   * @author chari on 05/26/2025
   */
  protected getBaseUrl(): string {
    return this._configService.get.call(this._configService, 'platform').baseUrl;
  }

  /**
   * This method is used to get the current user value
   * @author chari on 05/26/2025
   */
  public get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  get isAuthTokenReady(): boolean {
    return this.authTokenReadySubject.value;
  }

  /** Mark auth ready after generate-token succeeds and bearer token is stored. */
  markAuthTokenReady(): void {
    this.authTokenReadySubject.next(!!this.getBearerToken());
  }

  /** Block protected APIs while authorization is in progress. */
  clearAuthTokenReady(): void {
    this.authTokenReadySubject.next(false);
  }

  /** Restore auth for an existing session outside the authorization flow. */
  restoreAuthFromStorage(): void {
    if (this.isAuthorizationRouteActive()) {
      return;
    }

    if (this.getBearerToken()) {
      this.markAuthTokenReady();
    }
  }

  private isAuthorizationRouteActive(): boolean {
    return this._router.url.includes('/authorization') ||
      (typeof window !== 'undefined' && window.location.hash.includes('/authorization'));
  }

  /**
   * Returns the stored bearer token, or an empty string when unavailable.
   */
  getBearerToken(): string {
    const currentUser = localStorage.getItem('currentBIUser');
    if (!currentUser || currentUser === 'undefined') {
      return '';
    }

    try {
      return JSON.parse(currentUser || '{}').auth_token || '';
    } catch {
      return '';
    }
  }

  /**
   * This method is used to get the access token
   * @author chari on 05/26/2025
   */
  getAccessToken(token: string, redirectUrl: string) {
    this.clearAuthTokenReady();
    const url = this._configService.get.call(this._configService, 'platform').servicesBaseUrl;
    return this._http.post<any>(url + '/auth/generate-token/' + token + '/' + redirectUrl, []).pipe(
      map(data => {
        console.log(data);
        if (data.status === 1) {
          localStorage.setItem('currentBIUser', JSON.stringify({
            auth_token: data.userInfo,
            source_url: data.AtsUrl,
            ceipal_bi_upgraded: data.ceipalBiUpgraded,
            hide_back_to_old_bi: data.hideBackToOldBI
          }));
          this.markAuthTokenReady();
          this.currentUserSubject.next(data);
          if (redirectUrl != '') {
            this._router.navigate(['/' + data.redirectUrl]);
          } else {
            this._router.navigate(['/']);
          }
        } else {
          this._commonMethods.showToastMessage(data.message, '', Severity.error as Severity);
          setTimeout(() => {
            //this.logout();
          }, 200);
        }
        return data;
      })
    );
  }

  /**
   * This method is used to logout the current user
   * @author chari on 05/26/2025
   */
  logout() {
    let currentUser = localStorage.getItem('currentBIUser');
    if (currentUser) { //need tocheck the empty here
      let currentUserData = JSON.parse(currentUser!);
      let url = currentUserData?.source_url;
      
      // Add https: if not found
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      // remove user from local storage to log user out
      localStorage.removeItem('currentUser');
      this.currentUserSubject.next({});
    }
  }

  /**
   * Handles HTTP 401 responses by notifying the user and redirecting to sign-in.
   * Guarded so parallel failed requests only trigger one notification.
   */
  handleUnauthorizedSession(): void {
    if (this.isHandlingUnauthorizedSession) {
      return;
    }
    this.isHandlingUnauthorizedSession = true;
    this.clearAuthTokenReady();
    this._commonMethods.showToastMessage(
      AuthenticationService.UNAUTHORIZED_SESSION_MESSAGE,
      'Session Ended',
      Severity.warn
    );
    setTimeout(() => this.sessionExpiry(), 4000);
  }

  /**
   * This method is used to expiry the token 
   * @author chari on 05/26/2025
   */
  sessionExpiry() {
    console.log('sessionExpiry 93');
    this.clearAuthTokenReady();
    let currentUser = localStorage.getItem('currentBIUser');
    if (currentUser) { //need to check the empty 
      let currentUserData = JSON.parse(currentUser!);
      let thDomain = this._configService.get.call(this._configService, 'platform').thDomain;
      let url = currentUserData?.source_url ? currentUserData.source_url : thDomain;
      
      // Add https: if not found
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      // remove user from local storage to log user out
      localStorage.removeItem('currentUser');
      localStorage.removeItem('currentBIUser');
      this.currentUserSubject.next({});
      location.href = url + 'signin';
    } else {
      let url = this._configService.get.call(this._configService, 'platform').thDomain;
      
      // Add https: if not found
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      location.href = url + '/signin';
    }
  }

  /**
   * This method is used to update the current user data
   * @author chari on 11/12/2023
   */
  updateUserData(data: any) {
    this.currentUserSubject.next({ user_info: data });
  }

}
